import React from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { FaExternalLinkAlt, FaFilePdf, FaSyncAlt, FaFileInvoiceDollar } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { Button, Pill } from '../EntityList';
import { useLabels } from '../../hooks/useLabels';

const STATUS_TONES = {
	borrador: 'neutral',
	confirmado: 'ok',
	anulado: 'danger',
};

// `Button` renders a hardcoded <button>, so the "abrir borrador" link — which
// must stay an <a> to open GotaBilling in a new tab — borrows its class string.
const LINK_BUTTON_CLASS =
	'tw:inline-flex tw:items-center tw:gap-[7px] tw:rounded-[7px] tw:border tw:border-border-strong tw:bg-surface tw:px-2.5 tw:h-7 tw:text-[12.5px] tw:font-medium tw:text-ink-0 tw:no-underline tw:transition-[background,border-color] tw:duration-100 tw:hover:bg-surface-2 tw:hover:border-ink-3';

/**
 * Facturación state of a balance month: estado badge, serie/número + CAE once
 * emitted, links into GotaBilling (confirm draft / PDF), manual re-sync and
 * re-invoice after anulación. Rendered under the Balance toolbar whenever the
 * month has a comprobante.
 */
export default function FacturacionBalancePanel({ record, refreshing, onRefresh, onFacturarNuevamente, onDownloadPdf }) {
	const { t } = useLabels();
	if (!record) return null;

	const estado = record.estado || 'borrador';

	return (
		<div className='tw:rounded-panel tw:border tw:border-border tw:bg-surface tw:p-4 tw:shadow-sm'>
			<div className='tw:flex tw:flex-wrap tw:items-center tw:gap-2'>
				<span className='tw:flex tw:text-ink-2'><FaFileInvoiceDollar /></span>
				<strong className='tw:text-[13px] tw:font-semibold tw:text-ink-0'>
					{t('facturacion.panel.title')}
				</strong>
				<Pill tone={STATUS_TONES[estado] || 'neutral'} dot>
					{t(`facturacion.status.${estado}`)}
				</Pill>
				{record.tipoCfe && (
					<Pill tone='accent'>{t(`facturacion.tipoCfe.${record.tipoCfe}`)}</Pill>
				)}
				{estado === 'confirmado' && record.serie && (
					<span className='tw:text-[12.5px] tw:text-ink-2'>
						{t('facturacion.panel.serieNumero')}:{' '}
						<strong className='tw:font-mono tw:text-ink-1'>{record.serie}-{record.numero}</strong>
						{record.numeroCae && (
							<>
								{' · '}{t('facturacion.panel.cae')}:{' '}
								<span className='tw:font-mono tw:text-ink-1'>{record.numeroCae}</span>
							</>
						)}
					</span>
				)}
			</div>
			{estado === 'anulado' && record.motivoAnulacion && (
				<div className='tw:mt-1.5 tw:text-[12.5px] tw:text-ink-2'>
					{t('facturacion.panel.motivo')}: {record.motivoAnulacion}
				</div>
			)}
			<div className='tw:mt-3 tw:flex tw:flex-wrap tw:items-center tw:gap-2'>
				{estado === 'borrador' && record.links?.draft && (
					<a
						className={LINK_BUTTON_CLASS}
						href={record.links.draft}
						target='_blank'
						rel='noopener noreferrer'
					>
						<span className='tw:flex tw:text-[14px] tw:leading-none'><FaExternalLinkAlt /></span>
						{t('facturacion.panel.openDraft')}
					</a>
				)}
				{estado !== 'anulado' && onDownloadPdf && (
					<Button size='sm' icon={<FaFilePdf />} onClick={onDownloadPdf}>
						{t('facturacion.panel.pdf')}
					</Button>
				)}
				{estado !== 'anulado' && (
					<Button
						size='sm'
						icon={refreshing ? <Spinner animation='border' size='sm' /> : <FaSyncAlt />}
						onClick={onRefresh}
						disabled={refreshing}
					>
						{t('facturacion.refresh')}
					</Button>
				)}
				{estado === 'anulado' && onFacturarNuevamente && (
					<Button size='sm' icon={<FaFileInvoiceDollar />} onClick={onFacturarNuevamente}>
						{t('facturacion.panel.facturarNuevamente')}
					</Button>
				)}
			</div>
		</div>
	);
}

FacturacionBalancePanel.propTypes = {
	record: PropTypes.object,
	refreshing: PropTypes.bool,
	onRefresh: PropTypes.func,
	onFacturarNuevamente: PropTypes.func,
	onDownloadPdf: PropTypes.func,
};
